import React, { type AnchorHTMLAttributes, type ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'

type LinkQueryValue = string | number | boolean | Array<string | number | boolean>
type RouterLinkHrefObject = {
  pathname?: string
  query?: Record<string, LinkQueryValue>
  hash?: string
  search?: string
}

type AppLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string | URL | RouterLinkHrefObject
  replace?: boolean
  prefetch?: boolean
  scroll?: boolean
  locale?: string | false
  legacyBehavior?: boolean
}

function stringifyHref(href: string | URL | RouterLinkHrefObject): string {
  if (typeof href === 'string') {
    return href
  }

  if (href instanceof URL) {
    const path = href.pathname
    const query = href.searchParams.toString()
    const hash = href.hash
    return `${path}${query ? `?${query}` : ''}${hash}`
  }

  const basePath = href.pathname || ''
  const searchParams = new URLSearchParams()

  if (href.search) {
    const decoded = href.search.startsWith('?') ? href.search.substring(1) : href.search
    decoded
      .split('&')
      .filter(Boolean)
      .forEach((part) => {
        const [key, value = ''] = part.split('=')
        if (!key) return
        searchParams.append(decodeURIComponent(key), decodeURIComponent(value))
      })
  }

  if (href.query) {
    Object.entries(href.query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)))
      } else {
        searchParams.set(key, String(value))
      }
    })
  }

  const query = searchParams.toString()
  const hash = href.hash ? href.hash : ''
  const fullPath = `${basePath}${query ? `?${query}` : ''}${hash}`

  return fullPath
}

function isExternalHref(href: string): boolean {
  return (
    /^([a-z][a-z\d+.-]*:)?\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:')
  )
}

export default function AppLink({
  href,
  replace,
  prefetch: _prefetch,
  scroll: _scroll,
  locale: _locale,
  legacyBehavior: _legacyBehavior,
  ...props
}: AppLinkProps) {
  const normalizedHref = stringifyHref(href)

  if (isExternalHref(normalizedHref)) {
    return <a href={normalizedHref} {...props} />
  }

  const { children, ...linkProps } = props

  return (
    <RouterLink
      to={normalizedHref || '/'}
      replace={!!replace}
      {...(linkProps as unknown as Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>)}
    >
      {children as ReactNode}
    </RouterLink>
  )
}
